import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import useSeo, { ensureJsonLd } from '@/hooks/useSeo';
import { siteBrand, contactEmail, testimonials } from '@/mocks/manga';

const principles = [
  {
    icon: 'ri-focus-3-line',
    title: '先理主线，再看细节',
    desc: '我们从整体到局部整理剧情，先让你看懂作品在讲什么，再带你深入每一处值得回味的细节。',
  },
  {
    icon: 'ri-verified-badge-line',
    title: '忠于原作，不随意脑补',
    desc: '所有剧情梳理都以作品实际内容为准，尽量避免主观臆测，把客观的信息带给每一位读者。',
  },
  {
    icon: 'ri-shield-star-line',
    title: '条理清晰，方便检索',
    desc: '无论是按幕、按话还是按角色，我们都尽量做到层次分明，让查找剧情变得简单高效。',
  },
  {
    icon: 'ri-heart-line',
    title: '支持正版，热爱为驱动',
    desc: '我们仅做剧情介绍与交流，鼓励大家通过官方渠道阅读作品，用热爱支持创作者。',
  },
];

const milestones = [
  { num: '2019', text: '作品正式与读者见面，凭反差感十足的人设引起关注。' },
  { num: '2021', text: '同居日常与身世线逐渐展开，读者讨论度持续升温。' },
  { num: '2023', text: '感情线进入新阶段，成为同题材中口碑稳定的作品。' },
  { num: '2026', text: '剧情迈向新篇章，本站持续同步整理最新分集。' },
];

export default function About() {
  useSeo({
    title: '关于本站 泡在我家的辣妹剧情资讯站',
    description:
      '关于泡在我家的辣妹剧情资讯站：站点定位、发展理念与内容原则。我们专注整理泡在我家的辣妹漫画剧情、人物设定与分集剧情，陪伴每一位读者轻松追漫。',
    keywords: '关于泡在我家的辣妹,泡在我家的辣妹资讯站,泡在我家的辣妹漫画介绍',
    path: '/about',
  });

  useEffect(() => {
    ensureJsonLd('ld-breadcrumb', {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://dramaquest2.github.io/' },
        { '@type': 'ListItem', position: 2, name: '关于本站', item: 'https://dramaquest2.github.io/about' },
      ],
    });
    ensureJsonLd('ld-about', {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: '关于泡在我家的辣妹剧情资讯站',
      description: '本站专注整理泡在我家的辣妹漫画剧情主线、人物角色与分集剧情。',
      url: 'https://dramaquest2.github.io/about',
      email: contactEmail,
    });
    return () => {
      ['ld-breadcrumb', 'ld-about'].forEach((id) => document.getElementById(id)?.remove());
    };
  }, []);

  return (
    <>
      <Navbar />
      <main>
        <header className="bg-background-100">
          <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16 lg:px-8">
            <nav aria-label="面包屑" className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
              <Link to="/" className="hover:text-primary-600">首页</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-700">关于本站</span>
            </nav>
            <span className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
              <i className="ri-information-line"></i>
              关于本站
            </span>
            <h1 className="mt-4 text-2xl leading-snug text-foreground-950 md:text-4xl">
              用热爱，把每一段剧情讲清楚
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
              {siteBrand}剧情资讯站，是一个专注于整理这部漫画剧情、角色与分集的交流小站。我们希望每一位读者，都能在看漫画之前轻松进入状态。
            </p>
          </div>
        </header>

        <section className="bg-background-50 py-12 md:py-16">
          <div className="mx-auto max-w-7xl px-4 md:px-6 lg:px-8">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:items-center">
              <div>
                <h2 className="font-heading text-2xl text-foreground-950">我们是谁</h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
                  我们是一群被《泡在我家的辣妹》打动过的普通读者。因为喜欢，所以想把自己理清的角色设定、剧情主线与分集要点整理出来，分享给同样想追这部作品的朋友。这里没有复杂的盈利模式，只有一份想把剧情讲清楚的认真。
                </p>
                <h2 className="mt-8 font-heading text-2xl text-foreground-950">站点定位</h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
                  本站定位为一部漫画作品的剧情索引与导读：读者可以在这里快速了解故事讲了什么、有哪些主要角色、每一话进展到哪一步。我们专注于“让剧情更好懂”，不涉及资源下载或搬运。
                </p>
              </div>
              <div className="rounded-lg bg-background-100 p-6 md:p-8">
                <span className="inline-flex items-center gap-2 text-xs font-medium text-secondary-700">
                  <i className="ri-road-map-line"></i>
                  作品发展足迹
                </span>
                <ul className="mt-5 space-y-5">
                  {milestones.map((m) => (
                    <li key={m.num} className="flex gap-4">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent-100 font-heading text-sm text-accent-700">
                        {m.num}
                      </span>
                      <p className="text-sm leading-relaxed text-foreground-700">{m.text}</p>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-background-100 py-12 md:py-16">
          <div className="mx-auto max-w-7xl px-4 md:px-6 lg:px-8">
            <h2 className="font-heading text-2xl text-foreground-950">我们的内容原则</h2>
            <p className="mt-2 max-w-2xl text-sm text-foreground-600 md:text-base">
              为了让整理出来的剧情对读者真正有帮助，我们给自己定了四条基本准则。
            </p>
            <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {principles.map((p) => (
                <div key={p.title} className="rounded-lg bg-background-50 p-5">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                    <i className={`${p.icon} text-xl`}></i>
                  </span>
                  <h3 className="mt-3 font-heading text-lg text-foreground-900">{p.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{p.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-background-50 py-12 md:py-16">
          <div className="mx-auto max-w-7xl px-4 md:px-6 lg:px-8">
            <h2 className="font-heading text-2xl text-foreground-950">读者们怎么说</h2>
            <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-3">
              {testimonials.map((t) => (
                <figure key={t.name} className="rounded-lg bg-background-100 p-6">
                  <blockquote className="text-sm leading-relaxed text-foreground-700">“{t.content}”</blockquote>
                  <figcaption className="mt-4 flex items-center gap-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary-100 text-sm font-medium text-secondary-800">
                      {t.avatarText}
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{t.name}</p>
                      <p className="text-xs text-foreground-500">{t.role}</p>
                    </div>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-background-100 py-12 md:py-16">
          <div className="mx-auto max-w-7xl px-4 md:px-6 lg:px-8">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:items-center">
              <div>
                <h2 className="font-heading text-2xl text-foreground-950">联系我们</h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
                  如果你发现剧情梳理有误，或有想补充的内容，欢迎给我们来信。我们会认真阅读每一条建议，持续把站点做得更好。
                </p>
                <div className="mt-5 flex flex-wrap items-center gap-3">
                  <a
                    href={`mailto:${contactEmail}`}
                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-400"
                  >
                    <i className="ri-mail-line"></i>
                    {contactEmail}
                  </a>
                  <span className="inline-flex items-center gap-2 text-sm text-foreground-600">
                    <i className="ri-time-line"></i>
                    通常 1–3 个工作日内回复
                  </span>
                </div>
              </div>
              <div className="rounded-lg bg-primary-900 p-6 md:p-8">
                <h3 className="font-heading text-xl text-background-50">想更深入了解这部作品？</h3>
                <p className="mt-2 text-sm text-background-200">
                  从剧情主线、人物角色到分集剧情，我们已经帮你整理齐全。
                </p>
                <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                  <Link
                    to="/plot"
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-400"
                  >
                    查看剧情主线
                  </Link>
                  <Link
                    to="/characters"
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-50/30 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10"
                  >
                    了解人物角色
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}